import { z } from 'zod';

// Base API Response schema
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
      })
      .optional(),
    pagination: z
      .object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        totalPages: z.number(),
        hasMore: z.boolean(),
      })
      .optional(),
  });

// Admin Subscribers schema
export const SubscriberRowSchema = z.object({
  email: z.string().email(),
  source: z.enum(['newsletter', 'user']),
  subscribedAt: z.string(),
  displayName: z.string().nullable(),
  userId: z.string().nullable(),
});

export const AdminSubscribersDataSchema = z.object({
  subscribers: z.array(SubscriberRowSchema),
  total: z.number(),
  newsletterTotal: z.number(),
  optInTotal: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

// Location enrichment schema
export const LocationEnrichmentSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  wikipediaUrl: z.string().nullable(),
  population: z.number().nullable(),
  area: z.number().nullable(),
  timezone: z.string().nullable(),
  elevation: z.number().nullable(),
  description: z.string().nullable(),
  knownFor: z.array(z.string()).nullable(),
  climate: z.string().nullable(),
  enrichedAt: z.string(),
});

// Site strip schema
export const SiteStripSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  title: z.string(),
  excerpt: z.string(),
  url: z.string(),
  imageUrl: z.string().nullable(),
  source: z.string(),
  category: z.enum(['news', 'weather', 'events', 'tourism', 'emergency', 'other']),
  publishedAt: z.string().nullable(),
  scrapedAt: z.string(),
  isActive: z.boolean(),
});

export const AdminSiteStripsDataSchema = z.object({
  strips: z.array(SiteStripSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

// Ad Revenue schema
export const AdminAdRevenueDataSchema = z.object({
  sponsored: z.object({
    totalCampaigns: z.number(),
    activeCampaigns: z.number(),
    totalImpressions: z.number(),
    totalClicks: z.number(),
    ctr: z.number(),
    totalBudgetCents: z.number(),
    spentEstimateCents: z.number(),
  }),
  affiliate: z.object({
    totalProducts: z.number(),
    totalImpressions: z.number(),
    totalClicks: z.number(),
    ctr: z.number(),
  }),
  subscriptions: z.array(z.object({ tier: z.string(), count: z.number() })),
  audience: z.object({
    newsletterSubscribers: z.number(),
    marketingOptIn: z.number(),
  }),
  topCampaigns: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      partner: z.string(),
      impressions: z.number(),
      clicks: z.number(),
      ctr: z.number(),
      budget: z.number(),
      isActive: z.boolean(),
      startsAt: z.string(),
      endsAt: z.string(),
    })
  ),
  topAffiliates: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      category: z.string(),
      impressions: z.number(),
      clicks: z.number(),
      ctr: z.number(),
    })
  ),
});

// Schema registry for validated endpoints
export const VALIDATED_SCHEMAS = {
  '/admin/subscribers': AdminSubscribersDataSchema,
  '/admin/site-strips': AdminSiteStripsDataSchema,
  '/admin/ad-revenue': AdminAdRevenueDataSchema,
} as const;

export type ValidatedEndpoint = keyof typeof VALIDATED_SCHEMAS;

/**
 * Validate API response data against a Zod schema
 * @throws {Error} If validation fails
 */
export function validateResponse<T>(data: unknown, schema: z.ZodType<T>, endpoint: string): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    console.error(`API response validation failed for ${endpoint}:`, result.error);
    throw new Error(
      `Invalid API response from ${endpoint}: ${result.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join(', ')}`
    );
  }

  return result.data;
}
